import React, { useEffect, useState } from 'react';
import {
  CandidateAction,
  CandidateActionStatus,
} from 'features/Principal/types/candidatesListTypes';
import { ATSPrincipalAppCandidatesListTestIds } from 'data-testids/ATS';
import { getAxiosErrorMessageByStatusCode } from 'hooks/errorHandling/errorHandlingHooks';
import { useToast } from 'context/toastContext';
import { useUpdateCandidateStageMutation } from '../api/candidatesListMutations';
import { CandidateActionButtons } from './CandidateActionButtons';
import { trackPrincipalStageChanged } from 'utils/pendoTracking';

interface CandidateActionButtonsContainerProps {
  applicationId: number;
  initialStatus?: CandidateActionStatus;
  variant: 'list' | 'modal';
  onSuccess?: () => void;
}

export const CandidateActionButtonsContainer: React.FC<CandidateActionButtonsContainerProps> = ({
  applicationId,
  initialStatus,
  variant,
  onSuccess,
}) => {
  const [status, setStatus] = useState<CandidateActionStatus>(initialStatus ?? null);
  const mutation = useUpdateCandidateStageMutation(applicationId);
  const { showToast } = useToast();

  useEffect(() => {
    setStatus(initialStatus ?? null);
  }, [initialStatus]);

  const handleStatusChange = (newStatus: CandidateActionStatus) => {
    const previousStatus = status;
    setStatus(newStatus);
    mutation.mutate(newStatus, {
      onError: (error) => {
        setStatus(previousStatus);
        showToast(
          getAxiosErrorMessageByStatusCode(error) ?? 'An unexpected error occurred.',
          'error',
          ATSPrincipalAppCandidatesListTestIds.STAGE_UPDATE_ERROR_SNACKBAR
        );
      },
      onSuccess: () => {
        onSuccess?.();

        trackPrincipalStageChanged({
          application_id: applicationId,
          old_stage: previousStatus,
          new_stage: newStatus,
        });

        if (newStatus === CandidateAction.SELECTED_FOR_HIRE) {
          showToast(
            `Application Successfully Updated. Congrats! We've notified your administrator that you'd like to hire this candidate`,
            'success',
            ATSPrincipalAppCandidatesListTestIds.STAGE_UPDATE_SUCCESS_SNACKBAR
          );
        } else if (newStatus === CandidateAction.NOT_A_FIT) {
          showToast(
            `Application Successfully Updated`,
            'success',
            ATSPrincipalAppCandidatesListTestIds.STAGE_UPDATE_SUCCESS_SNACKBAR
          );
        }
      },
    });
  };

  return (
    <CandidateActionButtons variant={variant} status={status} onStatusChange={handleStatusChange} />
  );
};
